// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

// by moving the sort input inside the line itself

import QuoteTotal from "@adl/App/Components/CustomQuote/CustomQuoteTotalSummary";
import Notify from "@adl/App/Components/Notify/Notify";
import deleteQuoteLine from "@adl/App/Handlers/CustomQuote/DeleteQuoteLine";
import getQuoteDetails from "@adl/App/Handlers/CustomQuote/GetCustomQuote";
import updateQuoteLine from "@adl/App/Handlers/CustomQuote/UpdateQuoteLine";
import { CustomQuoteLinesModel, CustomQuoteModel, CustomQuoteProductInfo } from "@adl/App/Services/CustomQuotesService";
import { CustomAppState } from "@adl/App/Store/Reducers";
import MyCustomQuoteDetailsProductListLine from "@adl/App/Widgets/MyCustomQuoteDetails/MyCustomQuoteDetailsProductListLine";
import { color, xScreen } from "@adl/XDL/Utilities/";
import { maxLengthAttr } from "@adl/XDL/Utilities/validation";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import TwoButtonModal from "@insite/content-library/Components/TwoButtonModal";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Move from "@insite/mobius/Icons/Move";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import { PaginationPresentationProps } from "@insite/mobius/Pagination";
import TextArea, { TextAreaProps } from "@insite/mobius/TextArea";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useContext } from "react";
import { connect, ResolveThunks, useDispatch } from "react-redux";
import { SortableContainer, SortableElement, SortableHandle, SortEvent } from "react-sortable-hoc";
import { css } from "styled-components";

const mapStateToProps = (state: CustomAppState) => ({
    customQuote: state.xMyCustomQuoteDetails.quote,
    editingSortOrder: state.xMyCustomQuoteDetails.editingSortOrder,
    productsInfo: state.xMyCustomQuoteDetails.productsInfo,
    isLoading: state.xMyCustomQuoteDetails.isLoading,
    sortBy: state.xMyCustomQuoteDetails.sortBy,
});

const mapDispatchToProps = { updateQuoteLine, deleteQuoteLine, getQuoteDetails };

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface MyCustomQuoteDetailsProductListStyles {
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    wrapper?: InjectableCss;
    container?: GridContainerProps;
    lineGridItem?: GridItemProps;
    sortOrderWrapper?: InjectableCss;
    dragHandleIcon?: IconPresentationProps;
    editNotesModal?: ModalPresentationProps;
    paginationContainer?: GridContainerProps;
    paginationGridItem?: GridItemProps;
    pagination?: PaginationPresentationProps;
    notesTextArea?: TextAreaProps;
    editNotesModalButtonsWrapper?: InjectableCss;
    editNotesModalDeleteButton?: ButtonPresentationProps;
    editNotesModalCancelButton?: ButtonPresentationProps;
    editNotesModalSubmitButton?: ButtonPresentationProps;
}

export const productCustomQuoteStyles: MyCustomQuoteDetailsProductListStyles = {
    centeringWrapper: {
        css: css`
            height: 150px;
            display: flex;
            align-items: center;
        `,
    },
    wrapper: {
        css: css`
            & > span {
                margin-bottom: 16px;
            }
            @media print {
                margin-top: 30px;
            }
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    container: {
        gap: 0,
        offsetCss: css`
            @media print {
                margin-top: 10px;
            }
        `,
    },
    lineGridItem: {
        width: 12,
        css: css`
            position: relative;
        `,
    },
    sortOrderWrapper: {
        css: css`
            padding: 5px;
            position: absolute;
            background-color: ${color("common.ui4")};
            z-index: 1;
            line-height: 1;
        `,
    },
    dragHandleIcon: {
        src: Move,
        size: 22,
        css: css`
            cursor: move;
        `,
    },
    editNotesModal: { sizeVariant: "small" },
    editNotesModalButtonsWrapper: {
        css: css`
            padding-top: 30px;
            margin: -5px;
            display: flex;
            flex-direction: row-reverse;
            flex-wrap: wrap;
            justify-content: space-between;
            > button {
                margin: 5px;
                ${xScreen("lte", "xs")} {
                    width: calc(100% - 10px);
                }
            }
        `,
    },
    editNotesModalCancelButton: {
        variant: "secondary",
    },
};

const styles = productCustomQuoteStyles;

const MyCustomQuoteDetailsProductList: React.FC<Props> = ({
    customQuote,
    editingSortOrder,
    fields,
    updateQuoteLine,
    deleteQuoteLine,
    productsInfo,
    getQuoteDetails,
    isLoading,
    sortBy,
}) => {
    const toasterContext = useContext(ToasterContext);
    const [CustomQuoteLineToAction, setWishCustomQuoteLineToAction] = React.useState<CustomQuoteLinesModel | null>(
        null,
    );
    const [deleteLineModalIsOpen, setDeleteLineModalIsOpen] = React.useState(false);
    const [editNotesModalIsOpen, setEditNotesModalIsOpen] = React.useState(false);
    const { showImageBadges, showTextBadges, maximumNumberOfImageBadges, maximumNumberOfTextBadges } = fields;

    if (!customQuote || isLoading) {
        return null;
    }

    const CustomQuoteLines = customQuote.linesModel;

    const deleteCancelHandler = () => {
        setDeleteLineModalIsOpen(false);
    };

    const deleteSubmitHandler = () => {
        deleteQuoteLine({
            lineId: CustomQuoteLineToAction?.lineId,
            onSuccess() {
                deleteCancelHandler();
                getQuoteDetails({
                    quoteId: customQuote.id,
                    skipPricingAndInventory: false,
                    sortBy,
                });
            },
        });
    };

    const deleteClickHandler = (CustomQuoteLine: CustomQuoteLinesModel) => {
        setWishCustomQuoteLineToAction(CustomQuoteLine);
        setDeleteLineModalIsOpen(true);
    };

    const editNotesModalCancelHandler = () => {
        setEditNotesModalIsOpen(false);
    };

    const editNotesClickHandler = (CustomQuoteLine: CustomQuoteLinesModel) => {
        setWishCustomQuoteLineToAction(CustomQuoteLine);
        setEditNotesModalIsOpen(true);
    };

    const editNotesModalSubmitHandler = (notes: string) => {
        updateQuoteLine({
            updateNote: true,
            updateQty: false,
            updatePrice: false,
            linesModel: [
                {
                    lineId: CustomQuoteLineToAction?.lineId,
                    qty: CustomQuoteLineToAction?.qty,
                    note: notes,
                    price: CustomQuoteLineToAction?.extendedPrice,
                },
            ],
            onSuccess(result) {
                editNotesModalCancelHandler();
                getQuoteDetails({
                    quoteId: customQuote.id,
                    skipPricingAndInventory: true,
                    sortBy,
                });
            },
        });
    };

    const updateSortOrder = (customQuoteLine: CustomQuoteLinesModel, value: number | string) => {
        const sortOrder = parseInt(value.toString() || "1", 10);
        if (customQuoteLine.sortOrder === sortOrder) {
            return;
        }

        updateQuoteLine({
            updateNote: false,
            updateQty: false,
            updatePrice: false,
            updateSort: true,
            linesModel: [
                {
                    lineId: customQuoteLine?.lineId,
                    sortOrder,
                },
            ],
            onSuccess(result: any) {
                getQuoteDetails({
                    quoteId: customQuote.id,
                    skipPricingAndInventory: false,
                    sortBy,
                });
                toasterContext.addToast({ body: translate("Sort Order Updated"), messageType: "success" });
            },
            onError(error) {
                toasterContext.addToast({ body: error, messageType: "danger" });
            },
        });
    };

    const sortMove = (e: SortEvent) => {
        e.preventDefault();

        let clientY = 0;
        if ((e as any).clientY !== undefined) {
            clientY = (e as any).clientY;
        } else if ((e as any).targetTouches !== undefined) {
            clientY = (e as any).targetTouches[0].clientY;
        } else {
            return;
        }

        if (clientY < 50) {
            scroll(-20);
        } else if (clientY < 100) {
            scroll(-10);
        }

        const bottom = window.innerHeight - clientY;
        if (bottom < 50) {
            scroll(20);
        } else if (bottom < 100) {
            scroll(10);
        }
    };

    const scroll = (step: number) => {
        window.scrollBy(0, step);
    };

    if (!CustomQuoteLines || CustomQuoteLines.length === 0) {
        return (
            <StyledWrapper {...styles.wrapper}>
                <Notify type="info">{translate("No Products added to the list")}</Notify>
            </StyledWrapper>
        );
    }

    return (
        <>
            <LinesContainer
                CustomQuote={customQuote}
                CustomQuoteLines={CustomQuoteLines}
                productsInfo={productsInfo}
                editingSortOrder={editingSortOrder}
                canDrag={true}
                updateSortOrder={updateSortOrder}
                onDeleteClick={deleteClickHandler}
                onEditNotesClick={editNotesClickHandler}
                useDragHandle={true}
                lockAxis="y"
                lockToContainerEdges={true}
                lockOffset="0%"
                onSortStart={(_, e) => e.preventDefault()}
                onSortMove={sortMove}
                onSortEnd={({ oldIndex, newIndex }) => updateSortOrder(CustomQuoteLines[oldIndex], newIndex + 1)}
                showImageBadges={showImageBadges}
                showTextBadges={showTextBadges}
                maximumNumberOfImageBadges={maximumNumberOfImageBadges}
                maximumNumberOfTextBadges={maximumNumberOfTextBadges}
            />
            <QuoteTotal />
            <TwoButtonModal
                modalIsOpen={deleteLineModalIsOpen}
                headlineText={translate("Delete CustomQuote Item")}
                messageText={`${translate("Are you sure you want to delete this item?")}`}
                cancelButtonText={translate("Cancel")}
                submitButtonText={translate("Delete")}
                onCancel={deleteCancelHandler}
                onSubmit={deleteSubmitHandler}
                submitTestSelector="submitDeleteCustomQuoteItem"
            />
            {CustomQuoteLineToAction && (
                <Modal
                    {...styles.editNotesModal}
                    headline={translate(`${CustomQuoteLineToAction.notes ? "Edit" : "Add"} Notes`)}
                    isOpen={editNotesModalIsOpen}
                    handleClose={editNotesModalCancelHandler}
                >
                    <EditNotesForm
                        CustomQuoteLine={CustomQuoteLineToAction}
                        onCancel={editNotesModalCancelHandler}
                        onSubmit={editNotesModalSubmitHandler}
                    />
                </Modal>
            )}
        </>
    );
};

const DragHandle = SortableHandle(() => (
    <Icon {...styles.dragHandleIcon} data-test-selector="myCustomQuoteDetailPage_DragSortOrder" />
));

interface LineItemProps {
    CustomQuote: CustomQuoteModel;
    CustomQuoteLine: CustomQuoteLinesModel;
    productInfo?: CustomQuoteProductInfo;
    editingSortOrder: boolean;
    canDrag: boolean;
    updateSortOrder: (CustomQuoteLine: CustomQuoteLinesModel, value: number | string) => void;
    onDeleteClick: (CustomQuoteLine: CustomQuoteLinesModel) => void;
    onEditNotesClick: (CustomQuoteLine: CustomQuoteLinesModel) => void;
    showImageBadges: boolean;
    showTextBadges: boolean;
    maximumNumberOfImageBadges: number;
    maximumNumberOfTextBadges: number;
}

const LineItem = SortableElement<LineItemProps>(
    ({
        CustomQuote,
        CustomQuoteLine,
        productInfo,
        editingSortOrder,
        canDrag,
        updateSortOrder,
        onDeleteClick,
        onEditNotesClick,
        showImageBadges,
        showTextBadges,
        maximumNumberOfImageBadges,
        maximumNumberOfTextBadges,
    }: LineItemProps) => {
        if (!productInfo) {
            return null;
        }

        return (
            <GridItem {...styles.lineGridItem} data-test-selector="lineContainer">
                {editingSortOrder && (
                    <StyledWrapper {...styles.sortOrderWrapper}>{canDrag && <DragHandle />}</StyledWrapper>
                )}
                <MyCustomQuoteDetailsProductListLine
                    CustomQuote={CustomQuote}
                    CustomQuoteLine={CustomQuoteLine}
                    productInfo={productInfo}
                    onDeleteClick={onDeleteClick}
                    onEditNotesClick={onEditNotesClick}
                    showImageBadges={showImageBadges}
                    showTextBadges={showTextBadges}
                    maximumNumberOfImageBadges={maximumNumberOfImageBadges}
                    maximumNumberOfTextBadges={maximumNumberOfTextBadges}
                />
            </GridItem>
        );
    },
);

interface LinesContainerProps {
    CustomQuote: CustomQuoteModel;
    CustomQuoteLines: CustomQuoteLinesModel[];
    editingSortOrder: boolean;
    canDrag: boolean;
    updateSortOrder: (CustomQuoteLine: CustomQuoteLinesModel, value: number | string) => void;
    onDeleteClick: (CustomQuoteLine: CustomQuoteLinesModel) => void;
    onEditNotesClick: (CustomQuoteLine: CustomQuoteLinesModel) => void;
    showImageBadges: boolean;
    showTextBadges: boolean;
    maximumNumberOfImageBadges: number;
    maximumNumberOfTextBadges: number;
    productsInfo: CustomQuoteProductInfo[];
}

const LinesContainer = SortableContainer<LinesContainerProps>(
    ({
        CustomQuote,
        CustomQuoteLines,
        editingSortOrder,
        canDrag,
        updateSortOrder,
        onDeleteClick,
        onEditNotesClick,
        showImageBadges,
        showTextBadges,
        maximumNumberOfImageBadges,
        maximumNumberOfTextBadges,
        productsInfo,
    }: LinesContainerProps) => {
        return (
            <GridContainer {...styles.container} data-test-selector="myCustomQuoteDetails_lines">
                {CustomQuoteLines.map((CustomQuoteLine, index) => {
                    return (
                        <LineItem
                            key={`${CustomQuoteLine.productResult.id}`}
                            index={index}
                            CustomQuote={CustomQuote}
                            CustomQuoteLine={CustomQuoteLine}
                            productInfo={productsInfo[index]}
                            editingSortOrder={editingSortOrder}
                            canDrag={canDrag}
                            updateSortOrder={updateSortOrder}
                            onDeleteClick={onDeleteClick}
                            onEditNotesClick={onEditNotesClick}
                            showImageBadges={showImageBadges}
                            showTextBadges={showTextBadges}
                            maximumNumberOfImageBadges={maximumNumberOfImageBadges}
                            maximumNumberOfTextBadges={maximumNumberOfTextBadges}
                        />
                    );
                })}
            </GridContainer>
        );
    },
);

interface EditNotesFormProps {
    CustomQuoteLine: CustomQuoteLinesModel;
    onCancel: () => void;
    onSubmit: (notes: string) => void;
}

const EditNotesForm: React.FC<EditNotesFormProps> = ({ CustomQuoteLine, onCancel, onSubmit }) => {
    const [notes, setNotes] = React.useState(CustomQuoteLine.notes);
    const notesChangeHandler = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setNotes(
            event.target.value.length > maxLengthAttr.smallNote
                ? event.target.value.slice(0, maxLengthAttr.smallNote)
                : event.target.value,
        );
    };

    const deleteHandler = () => {
        setNotes("");
        onSubmit("");
    };

    return (
        <>
            <TextArea
                {...styles.notesTextArea}
                value={notes || ""}
                onChange={notesChangeHandler}
                hint={`${300 - notes.length} ${translate("characters left")}`}
                maxLength={maxLengthAttr.smallNote}
            />
            <StyledWrapper {...styles.editNotesModalButtonsWrapper}>
                {CustomQuoteLine.notes && (
                    <Button
                        {...styles.editNotesModalDeleteButton}
                        onClick={deleteHandler}
                        data-test-selector="myCustomQuote_deleteNoteButton"
                    >
                        {translate("Delete Note")}
                    </Button>
                )}
                <Button
                    {...styles.editNotesModalSubmitButton}
                    onClick={() => onSubmit(notes)}
                    data-test-selector="myCustomQuote_submitNoteButton"
                >
                    {translate(`${CustomQuoteLine.notes ? "Save" : "Add Notes"}`)}
                </Button>
                <Button
                    {...styles.editNotesModalCancelButton}
                    onClick={onCancel}
                    data-test-selector="myCustomQuote_cancelNoteButton"
                >
                    {translate("Cancel")}
                </Button>
            </StyledWrapper>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(MyCustomQuoteDetailsProductList),
    definition: {
        group: "Custom Quote Details" as any,
        displayName: "Product List",
        allowedContexts: ["CustomQuoteDetailsPage" as any],
    },
};

export default widgetModule;
